import { takeLatest, put, select, call } from "redux-saga/effects";
import {
  navigateToNewTestPage,
  navigateToSettingsPage,
  pressTerminateTestButton,
  pressTestOtherEyeButton,
  pressTurnOffDeviceButton,
  updateConfigSaveButtonState,
  toggleDoTheOtherEyeButtonState,
  confirmSkipVirtualAssistant,
  closeSkipVirtualAssistantModal,
  confirmSkipCalibration,
  closeSkipCalibrationModal,
} from "./uiSlice";
import {
  clearDoNotShowAgainList,
  clearTestRelatedData,
  setTestStatus,
  updateTestConfiguration,
  selectTest,
  updateSavedTestConfiguration,
} from "@store/tests/testSlice";
import { clearTestProgress } from "@actions/productActions";
import { navigate } from "@utils/navigationService";
import { getPreferredTestModeUrl } from "@utils/testModeStorage";
import { selectNewTestExtension } from "./uiSelectors";
import { PayloadAction } from "@reduxjs/toolkit";
import { calculateConfigButtonState } from "@utils/UIUtils";
import { EyeType, TestStatus, TestCategory } from "types/tests";
import {
  selectTestConfiguration,
  selectSavedTestConfiguration,
  getSelectedTestName,
} from "@store/tests/testSelectors";
import { waitForActions } from "@utils/sagaUtils";
import {
  getProductId,
  sendSkipCalibration,
  sendSkipVirtualAssistance,
} from "@utils/api-communication/WsUtil";
import { turnOffDevice } from "@utils/api-communication/RestApiCli";
import { selectIsSuiteRunning } from "@store/testSuite/testSuiteSelectors";

function* handleNavigateToNewTestPage() {
  const newTestExtension = yield select(selectNewTestExtension);
  if (newTestExtension) {
    yield navigate(newTestExtension);
  } else {
    console.warn("No new test extension found");
  }
}

function* handleNavigateToSettingsPage(action: PayloadAction<string | null>) {
  const orgId = action.payload;
  if (orgId) {
    yield navigate(`/organization-settings?orgId=${orgId}`);
  } else {
    yield navigate("/organization-settings");
  }
}

function* handleTerminateTestButtonPressed() {
  console.log("Terminate test button pressed");
  const isTestSuiteRunning = yield select(selectIsSuiteRunning);
  if (!isTestSuiteRunning) {
    yield put(clearDoNotShowAgainList());
  }
}

function* handlePressTestOtherEyeButton(action: PayloadAction<EyeType>) {
  yield put(toggleDoTheOtherEyeButtonState(true));
  const otherEyeType = action.payload;
  const currentConfiguration = yield select(selectTestConfiguration);

  if (!currentConfiguration) {
    console.error("Cannot test other eye: no current configuration");
    return;
  }

  yield put(clearTestProgress());
  yield put(clearTestRelatedData());

  yield put(
    updateTestConfiguration({
      ...currentConfiguration,
      eyeConfiguration: {
        ...currentConfiguration.eyeConfiguration,
        eyeType: otherEyeType,
        isBackToBackEyeTest: false,
      },
    }),
  );

  yield waitForActions(updateTestConfiguration.type, 500);

  yield put(setTestStatus({ status: TestStatus.StartRequested }));
}

function* handlePressTurnOffDeviceButton() {
  try {
    console.log("Turning off device");
    const productId = getProductId();

    if (!productId) {
      console.error("Cannot turn off device: no device selected.");
      return;
    }

    const response = yield call(() => {
      return new Promise((resolve, reject) => {
        turnOffDevice(
          { reboot: false, pid: productId },
          (result) => resolve({ data: result }),
          (error) => reject(error),
        );
      });
    });

    if (response.data === true || response.data === "true") {
      yield navigate(getPreferredTestModeUrl());
    } else {
      console.error("Failed to turn off device:", response);
    }
  } catch (error) {
    console.error("Error turning off device:", error);
  }
}

function* handleUpdateConfigButtonState() {
  const currentConfig = yield select(selectTestConfiguration);
  const savedConfigFull = yield select(selectSavedTestConfiguration);
  const testName: TestCategory = yield select(getSelectedTestName);

  if (!testName || !currentConfig) {
    return;
  }

  const relevantSavedConfig = savedConfigFull?.[testName];

  const buttonState = calculateConfigButtonState(currentConfig, relevantSavedConfig, testName);

  yield put(updateConfigSaveButtonState(buttonState));
}

function* handleConfirmSkipVirtualAssistant() {
  console.log("Skip Virtual Assistant confirmed - sending skip message");

  // Close the modal first
  yield put(closeSkipVirtualAssistantModal());

  // Send the skip message to Unity
  sendSkipVirtualAssistance();
}

function* handleConfirmSkipCalibration() {
  console.log("Skip Calibration confirmed - sending skip message");

  yield put(closeSkipCalibrationModal());

  sendSkipCalibration();
}

export default function* uiSagas() {
  yield takeLatest(navigateToNewTestPage, handleNavigateToNewTestPage);
  yield takeLatest(navigateToSettingsPage, handleNavigateToSettingsPage);
  yield takeLatest(pressTerminateTestButton, handleTerminateTestButtonPressed);
  yield takeLatest(pressTestOtherEyeButton, handlePressTestOtherEyeButton);
  yield takeLatest(pressTurnOffDeviceButton, handlePressTurnOffDeviceButton);
  yield takeLatest(updateTestConfiguration.type, handleUpdateConfigButtonState);
  yield takeLatest(selectTest.type, handleUpdateConfigButtonState);
  yield takeLatest(updateSavedTestConfiguration.type, handleUpdateConfigButtonState);
  yield takeLatest(confirmSkipVirtualAssistant.type, handleConfirmSkipVirtualAssistant);
  yield takeLatest(confirmSkipCalibration.type, handleConfirmSkipCalibration);
}
